import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { getCollectionsQuery } from '@/graphql/queries/getCollections';
import { Loader2 } from 'lucide-react';

interface FloatingActionButtonsWithCollectionProps {
  onSave?: (collectionId?: string) => void;
  onCancel?: () => void;
  showSaveControls?: boolean;
  initialCollectionId?: string;
  onCollectionChange?: (collectionId: string) => void;
}

// Hook to manage last used collection in localStorage
const useLastUsedCollection = () => {
  const [lastUsedCollectionId, setLastUsedCollectionId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastUsedCollection') || null;
    }
    return null;
  });

  const setLastUsedCollection = (collectionId: string) => {
    setLastUsedCollectionId(collectionId);
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastUsedCollection', collectionId);
    }
  };

  return { lastUsedCollectionId, setLastUsedCollection };
};

export const FloatingActionButtonsWithCollection: React.FC<FloatingActionButtonsWithCollectionProps> = ({
  onSave,
  onCancel,
  showSaveControls = false,
  initialCollectionId,
  onCollectionChange,
}) => {
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>(initialCollectionId || '');
  const { lastUsedCollectionId, setLastUsedCollection } = useLastUsedCollection();

  // Fetch all collections
  const { data: collectionsResponse, isLoading: collectionsLoading } = useQuery({
    queryKey: ['collections'],
    queryFn: getCollectionsQuery,
    staleTime: 5 * 60 * 1000,
  });
  const collections = collectionsResponse?.collections || [];

  // Set initial collection based on priority: initialCollectionId > lastUsedCollectionId > first collection
  useEffect(() => {
    if (!selectedCollectionId && collections.length > 0) {
      if (initialCollectionId) {
        setSelectedCollectionId(initialCollectionId);
      } else if (lastUsedCollectionId && collections.find(c => c.id === lastUsedCollectionId)) {
        setSelectedCollectionId(lastUsedCollectionId);
      } else {
        setSelectedCollectionId(collections[0].id);
      }
    }
  }, [collections, initialCollectionId, lastUsedCollectionId, selectedCollectionId]);

  // Handle collection change
  const handleCollectionChange = (value: string) => {
    setSelectedCollectionId(value);
    setLastUsedCollection(value);
    if (onCollectionChange) {
      onCollectionChange(value);
    }
  };

  // Handle save with collection
  const handleSave = () => {
    if (onSave) {
      // Pass the selected collection ID to the save handler
      onSave(selectedCollectionId);
    }
  };

  if (!showSaveControls) {
    return null;
  }

  return (
    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20">
      <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg p-3 flex gap-3 items-center">
        {/* Collection Dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Collection:</span>
          {collectionsLoading ? (
            <div className="flex items-center gap-2 px-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : collections.length === 0 ? (
            <span className="text-sm text-gray-500 px-3">No collections found</span>
          ) : (
            <Select
              value={selectedCollectionId}
              onValueChange={handleCollectionChange}
            >
              <SelectTrigger className="w-[200px] h-9 bg-white">
                <SelectValue placeholder="Select a collection" />
              </SelectTrigger>
              <SelectContent>
                {collections.map((collection) => (
                  <SelectItem key={collection.id} value={collection.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{collection.name}</span>
                      {collection.id === lastUsedCollectionId && collection.id !== initialCollectionId && (
                        <span className="text-xs text-gray-500 ml-2">(Recent)</span>
                      )}
                      {collection.id === initialCollectionId && (
                        <span className="text-xs text-blue-500 ml-2">(Current)</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300" />

        {/* Save/Cancel Buttons */}
        <Button
          onClick={handleSave}
          className="px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground"
          size="sm"
          disabled={collectionsLoading || collections.length === 0 || !selectedCollectionId}
        >
          Save Changes
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          className="px-6 py-2 bg-white hover:bg-gray-50 border border-gray-300"
          size="sm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};